import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Textarea } from '@/components/shadcn/textarea'
import { Switch } from '@/components/shadcn/switch'

import { useCurrentApplication } from '../useCurrentApplication'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

// REST API types
type MessagesDataAssetsDataAsset = RestSchemas['Messages.DataAssets.DataAsset']
type MessagesDataAssetsNewDataAsset = RestSchemas['Messages.DataAssets.NewDataAsset']
type MessagesEntityTypesExistingEntityType = RestSchemas['Messages.EntityTypes.ExistingEntityType']

// Zod validation schema
const formSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name cannot exceed 100 characters'),
  description: z.string().min(1, 'Description is required').max(200, 'Description cannot exceed 200 characters'),
  key: z.string().max(200, 'Key cannot exceed 200 characters').optional(),
  requireApprovals: z.boolean(),
})

export type TDataAssetForm = z.infer<typeof formSchema>

type DataAssetFormV2Props = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedDataAsset?: MessagesDataAssetsDataAsset
  selectedEntityType?: MessagesEntityTypesExistingEntityType
  // applicationId: number
}

export const DataAssetFormV2 = (props: DataAssetFormV2Props) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const edit = !!props.selectedDataAsset
  const { applicationId } = useCurrentApplication()

  // REST API client
  const { putDataAssetApiAddDataAsset, postDataAssetApiUpdateTemplate, getDataAssetApiListDataAssets } = useRestClient()

  // Update mutation
  const { mutate: updateDataAsset, isPending: isUpdating } = postDataAssetApiUpdateTemplate.useMutation(undefined, {
    onSuccess: () => {
      getDataAssetApiListDataAssets.invalidateQueries({})
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Data asset successfully updated')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error updating data asset')
    },
  })

  // Create mutation
  const { mutate: addDataAsset, isPending: isAdding } = putDataAssetApiAddDataAsset.useMutation(undefined, {
    onSuccess: () => {
      getDataAssetApiListDataAssets.invalidateQueries({})
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Data asset successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error adding data asset')
    },
  })

  // Submit handler
  const submitHandler = useCallback(
    (data: TDataAssetForm) => {
      if (edit && props.selectedDataAsset) {
        const updatedDataAsset: MessagesDataAssetsDataAsset = {
          id: props.selectedDataAsset.id,
          applicationId: props.selectedDataAsset.applicationId,
          entityTypeId: props.selectedDataAsset.entityTypeId,
          name: data.name,
          description: data.description,
          key: data.key || '',
          requireApprovals: data.requireApprovals,
        }
        updateDataAsset({ body: updatedDataAsset })
      } else {
        if (!props.selectedEntityType) {
          toast.error('Entity type is required for creating a new data asset')
          return
        }
        const newDataAsset: MessagesDataAssetsNewDataAsset = {
          applicationId: applicationId,
          entityTypeId: props.selectedEntityType.id,
          name: data.name,
          description: data.description,
          key: data.key || '',
          requireApprovals: data.requireApprovals,
        }
        addDataAsset({ body: newDataAsset })
      }
    },
    [edit, props.selectedDataAsset, props.selectedEntityType, applicationId, addDataAsset, updateDataAsset]
  )

  const isLoading = isUpdating || isAdding

  if (isLoading) return <FormComponentLoading />

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle || (edit ? 'Edit Data Asset' : 'Create Data Asset')}
      description={props.sheetDescription || (edit ? 'Update the data asset details' : 'Add a new data asset')}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} submitHandler={submitHandler} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} submitHandler={submitHandler} />
  )
}

const FormComponent = (
  props: DataAssetFormV2Props & {
    submitHandler: (data: TDataAssetForm) => void
  }
) => {
  const selectedDataAsset: Partial<MessagesDataAssetsDataAsset> = props.selectedDataAsset ?? {}
  const { name, description, key, requireApprovals } = selectedDataAsset
  const edit = !!props.selectedDataAsset

  const defaultValues = useMemo(() => {
    return {
      name: name ?? '',
      description: description ?? '',
      key: key ?? '',
      requireApprovals: requireApprovals ?? true,
    }
  }, [name, description, key, requireApprovals])

  const form = useForm<TDataAssetForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(props.submitHandler)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              {/* Name Field */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter data asset name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Description Field */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description *</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} placeholder="Enter data asset description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Key Field */}
              <FormField
                control={form.control}
                name="key"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Key</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter data asset key" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Require Approvals Switch */}
              <FormField
                control={form.control}
                name="requireApprovals"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Require Approvals</FormLabel>
                      <div className="text-sm text-muted-foreground">Enable approval workflow for this data asset</div>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="form-action-container">
              <Button type="submit">{edit ? 'Update Data Asset' : 'Create Data Asset'}</Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}
